import { getNodeType, InputRule, isNodeActive } from '@tiptap/core';
import type { DOMOutputSpec } from '@tiptap/pm/model';
import { Node_Type } from 'ricos-schema';
import bulletedListDataDefaults from 'ricos-schema/dist/statics/bulleted_list.defaults.json';
import { BulletedListNodeChildrenPattern } from 'ricos-schema/next';
import { addDefaultsInCorrectFormat } from 'wix-rich-content-common';
import { createRicosNodeExtension, withBreakoutContainer } from 'wix-rich-content-plugin-commons';

import { createListInputRuleHandler } from '../list-input-rule-handler';
import styles from '../statics/styles.scss';

export interface BulletListOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    bulletList: {
      /**
       * Toggle a bullet list
       */
      toggleBulletList: () => ReturnType;
      /**
       * Toggle off an bulleted list if exists
       */
      toggleOffBulletList: () => ReturnType;
    };
  }
}

export const inputRegex = /^\s*([-+*])\s$/;

export const bulletedList = createRicosNodeExtension<{
  HTMLAttributes: Record<string, string>;
  itemTypeName: string;
}>({
  groups: ['no-trailing-node'],
  type: 'html-node',
  name: Node_Type.BULLETED_LIST,
  createExtensionConfig({ mergeAttributes }) {
    return {
      name: this.name,
      addOptions() {
        return {
          HTMLAttributes: { class: styles.bulletedList },
          itemTypeName: Node_Type.LIST_ITEM,
        };
      },

      group: 'block list',

      content() {
        return BulletedListNodeChildrenPattern;
      },

      parseHTML() {
        return [{ tag: 'ul' }];
      },

      addAttributes() {
        return addDefaultsInCorrectFormat(bulletedListDataDefaults);
      },

      renderHTML({ HTMLAttributes }) {
        return withBreakoutContainer([
          'ul',
          mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
          0,
        ]) as DOMOutputSpec;
      },

      addCommands() {
        return {
          toggleBulletList:
            () =>
            ({ commands }) => {
              return commands.toggleLists(this.name, Node_Type.LIST_ITEM);
            },
          toggleOffBulletList:
            () =>
            ({ state, commands }) => {
              const isActive = isNodeActive(state, getNodeType(this.name, state.schema));
              if (isActive) {
                return commands.toggleBulletList();
              }
              return true;
            },
        };
      },

      addKeyboardShortcuts() {
        return {
          'Mod-Shift-8': () => this.editor.commands.toggleBulletList(),
        };
      },

      addInputRules() {
        return [
          new InputRule({
            find: inputRegex,
            handler: createListInputRuleHandler({ type: this.type }),
          }),
        ];
      },
    };
  },
});
